import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Leaf, BookOpen, Utensils, Home, CheckCircle2 } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const opportunities = [{
  icon: Users,
  title: 'Retreat Support',
  description: 'Assist with retreat logistics, welcome participants, and help create a nurturing environment for transformation.',
  commitment: '2-4 weeks'
}, {
  icon: Leaf,
  title: 'Garden & Grounds',
  description: 'Maintain our sacred gardens, walking paths, and outdoor meditation spaces with mindfulness and care.',
  commitment: 'Flexible'
}, {
  icon: Utensils,
  title: 'Kitchen & Dining',
  description: 'Prepare sattvic meals, serve food with love, and maintain our communal dining spaces.',
  commitment: '1-3 months'
}, {
  icon: BookOpen,
  title: 'Library & Archives',
  description: 'Organize spiritual texts, digitize teachings, and assist visitors in finding resources.',
  commitment: 'Flexible'
}, {
  icon: Home,
  title: 'Facilities Maintenance',
  description: 'Help maintain meditation halls, accommodations, and common areas in pristine condition.',
  commitment: '2-6 weeks'
}, {
  icon: Heart,
  title: 'Community Outreach',
  description: 'Support local community programs, teach basic meditation, and spread awareness of mindfulness.',
  commitment: '1-6 months'
}];
const benefits = ['Free accommodation in shared rooms', 'Three vegetarian meals daily', 'Participation in all meditation sessions', 'Access to teachings and discourses', 'Personal guidance from senior teachers', 'Certificate of service completion', 'Lifelong community connection', 'Skills development and training'];
export function VolunteerPage() {
  return <div className="min-h-screen bg-[#FFFBF5]" data-id="element-994">
      <Header data-id="element-995" />

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] w-full overflow-hidden" data-id="element-996">
        <motion.div initial={{
        scale: 1.1,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 1.2,
        ease: 'easeOut'
      }} className="absolute inset-0" data-id="element-997">
          <img src="https://images.unsplash.com/photo-1559027615-cd4628902d4a?q=80&w=2073&auto=format&fit=crop" alt="Volunteers working together" className="w-full h-full object-cover" data-id="element-998" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-[#FFFBF5]" data-id="element-999" />
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 pb-16" data-id="element-1000">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1001">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="max-w-4xl" data-id="element-1002">
              <h1 className="font-serif text-5xl md:text-7xl text-white mb-6 leading-tight drop-shadow-2xl" data-id="element-1003">
                Volunteer with Us
              </h1>
              <p className="text-xl md:text-2xl text-white/95 leading-relaxed drop-shadow-lg" data-id="element-1004">
                Serve, learn, and grow through karma yoga
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white" data-id="element-1005">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1006">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1007">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-8 text-center" data-id="element-1008">
              The Path of Selfless Service
            </h2>

            <div className="prose prose-lg max-w-none" data-id="element-1009">
              <p className="text-lg text-[#5D6D7E] leading-relaxed mb-6" data-id="element-1010">
                Volunteering at Supradhyan is more than just helping out—it's a
                profound spiritual practice known as karma yoga, the yoga of
                selfless action. By offering your time and energy without
                expectation of reward, you cultivate humility, compassion, and
                inner peace.
              </p>

              <p className="text-lg text-[#5D6D7E] leading-relaxed mb-6" data-id="element-1011">
                Our volunteers are an integral part of our community. They help
                maintain the sacred space, support retreat participants, and
                contribute to the daily operations that make transformation
                possible for thousands of seekers each year.
              </p>

              <p className="text-lg text-[#5D6D7E] leading-relaxed" data-id="element-1012">
                Whether you can offer a few weeks or several months, your
                service makes a meaningful difference while providing you with
                an immersive spiritual experience.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Opportunities */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-1013">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1014">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1015">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-4 text-center" data-id="element-1016">
              Volunteer Opportunities
            </h2>
            <p className="text-center text-[#5D6D7E] mb-12 max-w-2xl mx-auto" data-id="element-1017">
              Choose an area of service that resonates with your skills and
              interests
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1018">
              {opportunities.map((opportunity, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white p-6 rounded-xl shadow-md" data-id="element-1019">
                  <div className="w-12 h-12 rounded-full bg-[#8B9D83]/20 flex items-center justify-center mb-4" data-id="element-1020">
                    <opportunity.icon className="w-6 h-6 text-[#8B9D83]" data-id="element-1021" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-2" data-id="element-1022">
                    {opportunity.title}
                  </h3>
                  <p className="text-[#5D6D7E] mb-4" data-id="element-1023">
                    {opportunity.description}
                  </p>
                  <div className="text-sm text-[#8B9D83] font-semibold" data-id="element-1024">
                    Commitment: {opportunity.commitment}
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white" data-id="element-1025">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1026">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1027">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-12 text-center" data-id="element-1028">
              What You'll Receive
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1029">
              {benefits.map((benefit, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="flex items-start gap-3 bg-[#FAF7F2] p-4 rounded-lg" data-id="element-1030">
                  <CheckCircle2 className="w-5 h-5 text-[#8B9D83] flex-shrink-0 mt-0.5" data-id="element-1031" />
                  <span className="text-[#2C3E50]" data-id="element-1032">{benefit}</span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-1033">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1034">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1035">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-4 text-center" data-id="element-1036">
              Apply to Volunteer
            </h2>
            <p className="text-center text-[#5D6D7E] mb-12" data-id="element-1037">
              Fill out the form below and we'll be in touch within 48 hours
            </p>

            <form className="bg-white p-8 rounded-2xl shadow-lg space-y-6" data-id="element-1038">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1039">
                <div data-id="element-1040">
                  <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-1041">
                    First Name
                  </label>
                  <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" placeholder="John" data-id="element-1042" />
                </div>
                <div data-id="element-1043">
                  <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-1044">
                    Last Name
                  </label>
                  <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" placeholder="Doe" data-id="element-1045" />
                </div>
              </div>

              <div data-id="element-1046">
                <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-1047">
                  Email
                </label>
                <input type="email" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" placeholder="john@example.com" data-id="element-1048" />
              </div>

              <div data-id="element-1049">
                <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-1050">
                  Phone
                </label>
                <input type="tel" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" placeholder="+1 (555) 000-0000" data-id="element-1051" />
              </div>

              <div data-id="element-1052">
                <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-1053">
                  Area of Interest
                </label>
                <select className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" data-id="element-1054">
                  <option data-id="element-1055">Select an area</option>
                  <option data-id="element-1056">Retreat Support</option>
                  <option data-id="element-1057">Garden & Grounds</option>
                  <option data-id="element-1058">Kitchen & Dining</option>
                  <option data-id="element-1059">Library & Archives</option>
                  <option data-id="element-1060">Facilities Maintenance</option>
                  <option data-id="element-1061">Community Outreach</option>
                </select>
              </div>

              <div data-id="element-1062">
                <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-1063">
                  Availability
                </label>
                <select className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" data-id="element-1064">
                  <option data-id="element-1065">Select duration</option>
                  <option data-id="element-1066">2-4 weeks</option>
                  <option data-id="element-1067">1-3 months</option>
                  <option data-id="element-1068">3-6 months</option>
                  <option data-id="element-1069">6+ months</option>
                </select>
              </div>

              <div data-id="element-1070">
                <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-1071">
                  Why do you want to volunteer?
                </label>
                <textarea rows={4} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" placeholder="Share your motivation and what you hope to gain from this experience..." data-id="element-1072" />
              </div>

              <button type="submit" className="w-full bg-[#8B9D83] hover:bg-[#7A8C72] text-white py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg" data-id="element-1073">
                Submit Application
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-1074" />
    </div>;
}